import { SpendingLimitCategory } from '@components/card-control/types';
import { Card, DebitSpendingLimit } from '@generated-types';
import { KortbetjeningText } from '@hooks/use-text';
import { isChildCard, isTeenagerCard } from '@utils/card-type-utils';
import { formatAmount, prettyMonetary } from '@utils/text-format-utils';

const getSpendingLimitsWithUpdatedDailyAmount = (
    originalSpendingLimits: DebitSpendingLimit,
    newAmount: number,
): DebitSpendingLimit => ({
    ...originalSpendingLimits,
    card: {
        ...originalSpendingLimits.card,
        daily: {
            amount: newAmount,
            currency: originalSpendingLimits.card?.daily?.currency ?? 'NOK',
        },
    },
});

const getSpendingLimitsWithUpdatedMonthlyAmount = (
    originalSpendingLimits: DebitSpendingLimit,
    newAmount: number,
): DebitSpendingLimit => ({
    ...originalSpendingLimits,
    card: {
        ...originalSpendingLimits.card,
        monthly: {
            amount: newAmount,
            currency: originalSpendingLimits.card?.monthly?.currency ?? 'NOK',
        },
    },
});

const getSpendingLimitsWithUpdatedDailyEcommerceAmount = (
    originalSpendingLimits: DebitSpendingLimit,
    newAmount: number,
): DebitSpendingLimit => ({
    ...originalSpendingLimits,
    app: {
        ...originalSpendingLimits.card,
        daily: {
            amount: newAmount,
            currency: originalSpendingLimits.card?.daily?.currency ?? 'NOK',
        },
    },
});

const getSpendingLimitsWithUpdatedMonthlyEcommerceAmount = (
    originalSpendingLimits: DebitSpendingLimit,
    newAmount: number,
): DebitSpendingLimit => ({
    ...originalSpendingLimits,
    app: {
        ...originalSpendingLimits.card,
        monthly: {
            amount: newAmount,
            currency: originalSpendingLimits.card?.daily?.currency ?? 'NOK',
        },
    },
});

export const getSpendingLimitsWithUpdatedAmount = (
    originalSpendingLimits: DebitSpendingLimit,
    newAmount: number,
    category: SpendingLimitCategory,
) => {
    if (category === SpendingLimitCategory.DailyLimit) {
        return getSpendingLimitsWithUpdatedDailyAmount(originalSpendingLimits, newAmount);
    }

    if (category === SpendingLimitCategory.MonthlyLimit) {
        return getSpendingLimitsWithUpdatedMonthlyAmount(originalSpendingLimits, newAmount);
    }

    if (category === SpendingLimitCategory.DailyLimitEcommerce) {
        return getSpendingLimitsWithUpdatedDailyEcommerceAmount(originalSpendingLimits, newAmount);
    }

    return getSpendingLimitsWithUpdatedMonthlyEcommerceAmount(originalSpendingLimits, newAmount);
};

export const getAmountForCategory = (
    spendingLimits: DebitSpendingLimit | undefined,
    category: SpendingLimitCategory,
): number | undefined => {
    if (category === SpendingLimitCategory.DailyLimit) {
        return spendingLimits?.card?.daily?.amount;
    }

    if (category === SpendingLimitCategory.MonthlyLimit) {
        return spendingLimits?.card?.monthly?.amount;
    }

    if (category === SpendingLimitCategory.DailyLimitEcommerce) {
        return spendingLimits?.app?.daily?.amount;
    }

    if (category === SpendingLimitCategory.MonthlyLimitEcommerce) {
        return spendingLimits?.app?.monthly?.amount;
    }

    return undefined;
};

const getMonetaries = (spendingLimits: DebitSpendingLimit | undefined) => ({
    daily: spendingLimits?.card?.daily,
    monthly: spendingLimits?.card?.monthly,
    dailyEcommerce: spendingLimits?.app?.daily,
    monthlyEcommerce: spendingLimits?.app?.monthly,
});

/// //////// VALIDATION /////////////

const isDailyLimit = (category: SpendingLimitCategory) =>
    category === SpendingLimitCategory.DailyLimit || category === SpendingLimitCategory.DailyLimitEcommerce;

const isEcommerceLimit = (category: SpendingLimitCategory) =>
    category === SpendingLimitCategory.DailyLimitEcommerce || category === SpendingLimitCategory.MonthlyLimitEcommerce;

const amountSet = (amount: number | undefined): amount is number => typeof amount !== 'undefined';

const getInvalidAmountError = ({ category, amount, text, card, productSpendingLimits }: GetErrorProps) => {
    const maxAmount = getAmountForCategory(productSpendingLimits, category);

    if (!maxAmount) {
        return undefined;
    }

    if (amount > maxAmount) {
        const prettyMaxAmount = `${formatAmount(maxAmount, { currencyCode: 'NOK' })}`;

        if (isDailyLimit(category)) {
            if (isChildCard(card)) {
                return `${text.spendingLimitAmountChildError} ${prettyMaxAmount}`;
            }

            if (isTeenagerCard(card)) {
                return `${text.spendingLimitAmountTeenagerError} ${prettyMaxAmount}`;
            }

            return text.spendingLimitsDailyLimitExceededError;
        }

        if (isChildCard(card)) {
            return `${text.spendingLimitAmountChildError} ${prettyMaxAmount}`;
        }

        if (isTeenagerCard(card)) {
            return `${text.spendingLimitAmountTeenagerError} ${prettyMaxAmount}`;
        }

        return text.spendingLimitsMonthlyLimitExceededError;
    }

    return undefined;
};

interface InvalidErrorProps {
    amount: number | undefined;
    card: Card;
    category: SpendingLimitCategory;
    productSpendingLimits: DebitSpendingLimit;
    spendingLimits: DebitSpendingLimit;
    text: KortbetjeningText;
}

interface GetErrorProps extends InvalidErrorProps {
    amount: number;
}

const getPeriodIncongruenceError = ({ amount, spendingLimits, category, text }: GetErrorProps): string | undefined => {
    const { daily, monthly, dailyEcommerce, monthlyEcommerce } = getMonetaries(spendingLimits);

    if (isEcommerceLimit(category)) {
        if (isDailyLimit(category)) {
            if (monthlyEcommerce && amount > monthlyEcommerce.amount) {
                return `${text.spendingLimitsDiffDailyError} (${prettyMonetary(monthlyEcommerce)})`;
            }

            return undefined;
        }

        if (dailyEcommerce && amount < dailyEcommerce.amount) {
            return `${text.spendingLimitsDiffMonthlyError} (${prettyMonetary(dailyEcommerce)})`;
        }
    }

    if (isDailyLimit(category)) {
        if (monthly && amount > monthly.amount) {
            return `${text.spendingLimitsDiffDailyError} (${prettyMonetary(monthly)})`;
        }

        return undefined;
    }

    if (daily && amount < daily.amount) {
        return `${text.spendingLimitsDiffMonthlyError} (${prettyMonetary(daily)})`;
    }

    return undefined;
};

const getChildTypeError = ({ category, card, text }: InvalidErrorProps): string | undefined => {
    if (isChildCard(card) && isEcommerceLimit(category)) {
        return text.spendingLimitTypeChildError;
    }

    return undefined;
};

export const getInvalidError = (props: InvalidErrorProps): string | undefined => {
    if (!amountSet(props.amount)) {
        return isDailyLimit(props.category)
            ? props.text.spendingLimitsDailyLimitNotSetError
            : props.text.spendingLimitsMonthlyLimitNotSetError;
    }

    return (
        getChildTypeError({ ...props, amount: props.amount }) ??
        getInvalidAmountError({ ...props, amount: props.amount }) ??
        getPeriodIncongruenceError({ ...props, amount: props.amount })
    );
};
